import React, { useState, useEffect, useMemo } from 'react';
import { useCollect, useRequest } from 'hooks';
import { useParams } from 'react-router-dom';

import Modal from 'components/Modal';
import { Button, Datepicker, Select } from 'components/Misc/Form';

import Course from 'containers/Administrator/Grades/Profile/ReStudents/Transfer/Course';

import { useGradeStudentStore } from 'store/grade/useGradeStudentStore';
import { useGradeStudentsTransferStore } from 'store/grade/useGradeStudentsTransferStore';
import { GRADE_STUDENT_DIRECTIONS } from 'utils/Constants';

const Transfer = () => {
	// Cache current grade's id
	const { id } = useParams();

	// Store modal's visibility
	const [modal, setModal] = useState(false);

	// Use student store
	const [options, parameters] = useGradeStudentStore(state => [
		state.options,
		state.parameters
	]);

	// Use student assign store
	const [data, grades, blanks, onChange, onGenerate, onInitGrades, onInitBlanks] = useGradeStudentsTransferStore(state => [
		state.data,
		state.grades,
		state.blanks,
		state.onChange,
		state.onGenerate,
		state.onInitGrades,
		state.onInitBlanks
	]);

	// Store request parameters
	const params = useMemo(() => ({
		gradeId: data.gradeId,
		eager: 'courses.[subject, exam]'
	}), [data.gradeId]);

	// Store collected grades
	const [, collectGrades] = useCollect('grades', null, onInitGrades);

	// Store collected blanks
	const [, collectBlanks] = useCollect('blanks', params, onInitBlanks);

	// Store request
	const [[progress], request] = useRequest('put', `grades/${id}`);

	// Handle modal's visibility toggle
	const onModal = () => setModal(prev => !prev);

	// Handle student submition
	const onSubmit = async () => {
		// Try to assign students to course
		const [success] = await request({
			...data,
			options
		}, {
			...parameters,
			related: 'students:transfer'
		});

		// Reset on success
		if (success) onModal();
	};

	// Collect grades on mount
	useEffect(() => {
		(async () => await collectGrades())();
	}, [collectGrades]);

	// Collect blanks on grade selection
	useEffect(() => {
		if (params.gradeId) (async () => await collectBlanks())();
	}, [params, collectBlanks]);

	return (
		<>
			<Button
				theme="success"
				value="Pārcelt studentus"
				onClick={onModal}
			/>

			<Modal title="Pārcelt studentus" visible={modal} handleModal={onModal}>
				<div className="grid grid-cols-2 gap-4">
					<Select
						name="gradeId"
						label="Klase"
						placeholder=" "
						value={data.gradeId}
						onChange={onChange}
						options={grades?.map(grade => ({
							value: grade.id,
							name: grade.title
						}))}
					/>

					<Select
						name="blankId"
						label="Sagatave"
						placeholder=" "
						disabled={!blanks?.length}
						value={data.blankId}
						onChange={onChange}
						options={blanks?.map(blank => ({
							value: blank.id,
							name: blank.title
						}))}
					/>

					<Select
						name="direction"
						label="Novirziens"
						placeholder=" "
						value={data.direction}
						onChange={onChange}
						options={GRADE_STUDENT_DIRECTIONS?.map(direction => ({
							value: direction.value,
							name: direction.text
						}))}
					/>

					<div />

					<Datepicker
						asInput
						name="startDate"
						label="Sākuma datums"
						value={data.startDate}
						onChange={onChange}
					/>

					<Datepicker
						end
						asInput
						name="endDate"
						label="Beigu datums"
						value={data.endDate}
						onChange={onChange}
					/>
				</div>
				
				<div className="mt-4 flex justify-between items-center">
					<div className="text-neutral-900 dark:text-neutral-100 text-sm font-semibold">
						Mācību grafiks
					</div>

					<Button
						small
						square
						theme="success"
						icon="double_arrow"
						onClick={onGenerate}
					/>
				</div>

				<div className="mt-4 grid grid-cols-2 gap-4">
					{data.courses.map(course =>
						<Course key={course.id} course={course} />
					)}
				</div>

				<div className="mt-4 flex justify-end">
					<Button
						progress={progress}
						theme="success"
						value="Pārcelt studentus"
						onClick={onSubmit}
					/>
				</div>
			</Modal>
		</>
	);
};

export default Transfer;